import React, { useState, useEffect } from 'react';
import {
  Card,
  Table,
  Button,
  Modal,
  Form,
  Input,
  Select,
  InputNumber,
  message,
  Typography,
  Space,
  Tag,
  Row,
  Col,
  Statistic,
  Popconfirm,
  Tabs,
  Alert
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  ShopOutlined,
  DollarOutlined,
  WarningOutlined,
  BarChartOutlined,
  InboxOutlined,
  ArrowUpOutlined,
  FileExcelOutlined
} from '@ant-design/icons';
import { stationOwnerAPI } from '../services/api';
import { handleExport } from '../utils/exportUtils';
import dayjs from 'dayjs';

const { Title } = Typography;
const { Option } = Select;
const { TextArea } = Input;

interface InventoryItem {
  _id: string;
  productCode: string;
  productName: string;
  productType: string;
  category?: string;
  brand?: string;
  currentStock: number;
  reservedStock: number;
  availableStock: number;
  unit: string;
  minStockLevel: number;
  maxStockLevel?: number;
  costPrice: number;
  sellingPrice: number;
  supplier?: {
    _id: string;
    companyName: string;
  };
  station: {
    _id: string;
    name: string;
  };
  isActive: boolean;
}

interface InventoryTransaction {
  _id: string;
  transactionNumber: string;
  transactionType: string;
  quantity: number;
  unitPrice: number;
  stockBefore: number;
  stockAfter: number;
  description?: string;
  transactionDate: string;
  inventory: {
    productCode: string;
    productName: string;
  };
}

const Inventory: React.FC = () => {
  const [inventories, setInventories] = useState<InventoryItem[]>([]);
  const [transactions, setTransactions] = useState<InventoryTransaction[]>([]);
  const [stations, setStations] = useState<any[]>([]);
  const [suppliers, setSuppliers] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [transactionsLoading, setTransactionsLoading] = useState(false);
  const [inventoryModalVisible, setInventoryModalVisible] = useState(false);
  const [transactionModalVisible, setTransactionModalVisible] = useState(false);
  const [reportModalVisible, setReportModalVisible] = useState(false);
  const [selectedInventory, setSelectedInventory] = useState<InventoryItem | null>(null);
  const [activeTab, setActiveTab] = useState('inventory');
  const [filters, setFilters] = useState<any>({
    stationId: 'all',
    productType: 'all',
    lowStock: 'false',
    search: ''
  });
  const [stockReport, setStockReport] = useState<any>(null);
  const [form] = Form.useForm();
  const [transactionForm] = Form.useForm();
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  useEffect(() => {
    fetchStations();
    fetchSuppliers();
    fetchInventories();
    if (activeTab === 'transactions') {
      fetchTransactions();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, filters]);

  const fetchStations = async () => {
    try {
      const response = await stationOwnerAPI.getStations();
      setStations(response);
    } catch (error) {
      message.error('İstasyonlar yüklenemedi');
    }
  };

  const fetchSuppliers = async () => {
    try {
      const response = await stationOwnerAPI.getSuppliers({ isActive: 'true' });
      setSuppliers(response);
    } catch (error) {
      // Tedarikçiler opsiyonel
    }
  };

  const fetchInventories = async () => {
    setLoading(true);
    try {
      const params: any = {};
      if (filters.stationId !== 'all') params.stationId = filters.stationId;
      if (filters.productType !== 'all') params.productType = filters.productType;
      if (filters.lowStock === 'true') params.lowStock = 'true';
      if (filters.search) params.search = filters.search;

      const response = await stationOwnerAPI.getInventories(params);
      setInventories(response);
    } catch (error: any) {
      message.error(error.response?.data?.message || 'Envanter yüklenemedi');
    } finally {
      setLoading(false);
    }
  };

  const fetchTransactions = async () => {
    setTransactionsLoading(true);
    try {
      const params: any = {};
      if (filters.stationId !== 'all') params.stationId = filters.stationId;

      const response = await stationOwnerAPI.getInventoryTransactions(params);
      setTransactions(response);
    } catch (error: any) {
      message.error(error.response?.data?.message || 'İşlemler yüklenemedi');
    } finally {
      setTransactionsLoading(false);
    }
  };

  const fetchStockReport = async () => {
    try {
      const params: any = {};
      if (filters.stationId !== 'all') params.stationId = filters.stationId;
      if (filters.productType !== 'all') params.productType = filters.productType;

      const response = await stationOwnerAPI.getStockReport(params);
      setStockReport(response);
      setReportModalVisible(true);
    } catch (error: any) {
      message.error(error.response?.data?.message || 'Rapor yüklenemedi');
    }
  };

  const handleAddInventory = () => {
    setSelectedInventory(null);
    form.resetFields();
    form.setFieldsValue({
      productType: 'fuel',
      unit: 'litre',
      currentStock: 0,
      reservedStock: 0,
      minStockLevel: 0,
      costPrice: 0,
      sellingPrice: 0,
      isActive: true,
      isTracked: true
    });
    setInventoryModalVisible(true);
  };

  const handleEditInventory = (inventory: InventoryItem) => {
    setSelectedInventory(inventory);
    form.setFieldsValue({
      ...inventory,
      stationId: inventory.station._id,
      supplierId: inventory.supplier?._id
    });
    setInventoryModalVisible(true);
  };

  const handleSubmitInventory = async (values: any) => {
    try {
      const data = {
        ...values,
        stationId: values.stationId,
        supplierId: values.supplierId || null
      };

      if (selectedInventory) {
        await stationOwnerAPI.updateInventory(selectedInventory._id, data);
        message.success('Envanter güncellendi');
      } else {
        await stationOwnerAPI.createInventory(data);
        message.success('Envanter ürünü oluşturuldu');
      }
      setInventoryModalVisible(false);
      form.resetFields();
      setSelectedInventory(null);
      fetchInventories();
    } catch (error: any) {
      message.error(error.response?.data?.message || 'İşlem başarısız');
    }
  };

  const handleDeleteInventory = async (id: string) => {
    try {
      await stationOwnerAPI.deleteInventory(id);
      message.success('Envanter ürünü silindi');
      fetchInventories();
    } catch (error: any) {
      message.error(error.response?.data?.message || 'İşlem başarısız');
    }
  };

  const handleAddTransaction = (inventory?: InventoryItem) => {
    transactionForm.resetFields();
    if (inventory) {
      transactionForm.setFieldsValue({
        inventoryId: inventory._id,
        transactionType: 'receive',
        quantity: 0,
        unitPrice: inventory.costPrice || 0,
        transactionDate: dayjs()
      });
    } else {
      transactionForm.setFieldsValue({
        transactionType: 'receive',
        quantity: 0,
        transactionDate: dayjs()
      });
    }
    setTransactionModalVisible(true);
  };

  const handleSubmitTransaction = async (values: any) => {
    try {
      await stationOwnerAPI.processStockTransaction({
        inventoryId: values.inventoryId,
        transactionType: values.transactionType,
        quantity: values.quantity,
        unitPrice: values.unitPrice || 0,
        description: values.description,
        notes: values.notes,
        referenceType: values.referenceType || 'other',
        supplierId: values.supplierId,
        customerId: values.customerId
      });
      message.success('Stok işlemi başarıyla gerçekleştirildi');
      setTransactionModalVisible(false);
      transactionForm.resetFields();
      fetchInventories();
      if (activeTab === 'transactions') {
        fetchTransactions();
      }
    } catch (error: any) {
      message.error(error.response?.data?.message || 'İşlem başarısız');
    }
  };

  const inventoryColumns = [
    {
      title: 'Ürün Kodu',
      dataIndex: 'productCode',
      key: 'productCode',
      render: (text: string) => <span style={{ fontWeight: 'bold' }}>{text}</span>
    },
    {
      title: 'Ürün Adı',
      dataIndex: 'productName',
      key: 'productName',
      render: (text: string, record: InventoryItem) => (
        <Space direction="vertical" size={0}>
          <span>{text}</span>
          {record.brand && (
            <span style={{ fontSize: '12px', color: '#666' }}>{record.brand}</span>
          )}
        </Space>
      )
    },
    {
      title: 'İstasyon',
      dataIndex: ['station', 'name'],
      key: 'station'
    },
    {
      title: 'Tip',
      dataIndex: 'productType',
      key: 'productType',
      render: (type: string) => {
        const colors: any = {
          fuel: 'red',
          lubricant: 'blue',
          market: 'green',
          car_wash: 'orange',
          restaurant: 'purple',
          equipment: 'cyan',
          other: 'default'
        };
        const labels: any = {
          fuel: 'Yakıt',
          lubricant: 'Yağ',
          market: 'Market',
          car_wash: 'Oto Yıkama',
          restaurant: 'Restoran',
          equipment: 'Ekipman',
          other: 'Diğer'
        };
        return <Tag color={colors[type]}>{labels[type] || type}</Tag>;
      }
    },
    {
      title: 'Mevcut Stok',
      dataIndex: 'currentStock',
      key: 'currentStock',
      render: (stock: number, record: InventoryItem) => {
        const isLow = stock <= record.minStockLevel;
        return (
          <span style={{ color: isLow ? '#ff4d4f' : 'inherit', fontWeight: isLow ? 'bold' : 'normal' }}>
            {stock} {record.unit}
            {isLow && <WarningOutlined style={{ marginLeft: 4, color: '#ff4d4f' }} />}
          </span>
        );
      }
    },
    {
      title: 'Maliyet',
      dataIndex: 'costPrice',
      key: 'costPrice',
      render: (price: number) => `${price.toLocaleString('tr-TR')} ₺`
    },
    {
      title: 'Satış Fiyatı',
      dataIndex: 'sellingPrice',
      key: 'sellingPrice',
      render: (price: number) => `${price.toLocaleString('tr-TR')} ₺`
    },
    {
      title: 'Durum',
      dataIndex: 'isActive',
      key: 'isActive',
      render: (isActive: boolean) => (
        <Tag color={isActive ? 'success' : 'default'}>
          {isActive ? 'Aktif' : 'Pasif'}
        </Tag>
      )
    },
    {
      title: 'İşlemler',
      key: 'actions',
      render: (_: any, record: InventoryItem) => (
        <Space>
          <Button
            type="link"
            icon={<ArrowUpOutlined />}
            onClick={() => handleAddTransaction(record)}
            title="Stok İşlemi"
          />
          <Button
            type="link"
            icon={<EditOutlined />}
            onClick={() => handleEditInventory(record)}
          />
          <Popconfirm
            title="Bu ürünü silmek istediğinizden emin misiniz?"
            onConfirm={() => handleDeleteInventory(record._id)}
          >
            <Button type="link" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      )
    }
  ];

  const transactionColumns = [
    {
      title: 'İşlem No',
      dataIndex: 'transactionNumber',
      key: 'transactionNumber',
      render: (text: string) => <span style={{ fontWeight: 'bold' }}>{text}</span>
    },
    {
      title: 'Ürün',
      key: 'product',
      render: (record: InventoryTransaction) => (
        <Space direction="vertical" size={0}>
          <span>{record.inventory.productCode}</span>
          <span style={{ fontSize: '12px', color: '#666' }}>{record.inventory.productName}</span>
        </Space>
      )
    },
    {
      title: 'Tip',
      dataIndex: 'transactionType',
      key: 'transactionType',
      render: (type: string) => {
        const colors: any = {
          receive: 'green',
          sale: 'blue',
          return: 'orange',
          adjustment: 'purple',
          transfer: 'cyan',
          waste: 'red',
          damage: 'red'
        };
        const labels: any = {
          receive: 'Alış',
          sale: 'Satış',
          return: 'İade',
          adjustment: 'Düzeltme',
          transfer: 'Transfer',
          waste: 'Fire',
          damage: 'Hasarlı'
        };
        return <Tag color={colors[type]}>{labels[type] || type}</Tag>;
      }
    },
    {
      title: 'Miktar',
      dataIndex: 'quantity',
      key: 'quantity',
      render: (qty: number, record: InventoryTransaction) => (
        <span style={{ fontWeight: 'bold' }}>
          {record.transactionType === 'sale' || record.transactionType === 'waste' || record.transactionType === 'damage' ? '-' : '+'}
          {qty}
        </span>
      )
    },
    {
      title: 'Önceki',
      dataIndex: 'stockBefore',
      key: 'stockBefore'
    },
    {
      title: 'Sonraki',
      dataIndex: 'stockAfter',
      key: 'stockAfter',
      render: (stock: number) => <span style={{ fontWeight: 'bold' }}>{stock}</span>
    },
    {
      title: 'Birim Fiyat',
      dataIndex: 'unitPrice',
      key: 'unitPrice',
      render: (price: number) => `${price.toLocaleString('tr-TR')} ₺`
    },
    {
      title: 'Tarih',
      dataIndex: 'transactionDate',
      key: 'transactionDate',
      render: (date: string) => dayjs(date).format('DD MMM YYYY HH:mm')
    }
  ];

  const totalProducts = inventories.length;
  const lowStockCount = inventories.filter(inv => inv.currentStock <= inv.minStockLevel).length;
  const outOfStockCount = inventories.filter(inv => inv.currentStock === 0).length;
  const totalValue = inventories.reduce((sum, inv) => sum + (inv.currentStock * inv.costPrice), 0);

  const tabItems = [
    {
      key: 'inventory',
      label: 'Envanter',
      children: (
        <>
          {/* Özet İstatistikler */}
          <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
            <Col xs={24} sm={12} lg={6}>
              <Card>
                <Statistic
                  title="Toplam Ürün"
                  value={totalProducts}
                  prefix={<ShopOutlined />}
                />
              </Card>
            </Col>
            <Col xs={24} sm={12} lg={6}>
              <Card>
                <Statistic
                  title="Düşük Stok"
                  value={lowStockCount}
                  prefix={<WarningOutlined />}
                  valueStyle={{ color: '#ff4d4f' }}
                />
              </Card>
            </Col>
            <Col xs={24} sm={12} lg={6}>
              <Card>
                <Statistic
                  title="Stokta Yok"
                  value={outOfStockCount}
                  prefix={<InboxOutlined />}
                  valueStyle={{ color: '#fa8c16' }}
                />
              </Card>
            </Col>
            <Col xs={24} sm={12} lg={6}>
              <Card>
                <Statistic
                  title="Toplam Değer"
                  value={totalValue}
                  prefix={<DollarOutlined />}
                  precision={0}
                  suffix="₺"
                />
              </Card>
            </Col>
          </Row>

          {/* Düşük Stok Uyarıları */}
          {lowStockCount > 0 && (
            <Alert
              message={`${lowStockCount} ürün düşük stok seviyesinde`}
              type="warning"
              showIcon
              style={{ marginBottom: 24 }}
              action={
                <Button size="small" onClick={() => setFilters({ ...filters, lowStock: 'true' })}>
                  Görüntüle
                </Button>
              }
            />
          )}

          {/* Filtreler */}
          <Card style={{ marginBottom: 24 }} styles={{ body: { padding: isMobile ? '12px' : '16px' } }}>
            <Row gutter={[8, 8]}>
              <Col xs={24} sm={12} md={6}>
                <Input
                  placeholder="Ara (ürün kodu, adı)"
                  value={filters.search}
                  onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                  allowClear
                  size={isMobile ? 'small' : 'middle'}
                />
              </Col>
              <Col xs={24} sm={12} md={6}>
                <Select
                  value={filters.stationId}
                  onChange={(value) => setFilters({ ...filters, stationId: value })}
                  style={{ width: '100%' }}
                  placeholder="İstasyon"
                  size={isMobile ? 'small' : 'middle'}
                >
                  <Option value="all">Tüm İstasyonlar</Option>
                  {stations.map((station: any) => (
                    <Option key={station._id} value={station._id}>
                      {station.name}
                    </Option>
                  ))}
                </Select>
              </Col>
              <Col xs={24} sm={12} md={6}>
                <Select
                  value={filters.productType}
                  onChange={(value) => setFilters({ ...filters, productType: value })}
                  style={{ width: '100%' }}
                  placeholder="Ürün Tipi"
                  size={isMobile ? 'small' : 'middle'}
                >
                  <Option value="all">Tümü</Option>
                  <Option value="fuel">Yakıt</Option>
                  <Option value="lubricant">Yağ</Option>
                  <Option value="market">Market</Option>
                  <Option value="car_wash">Oto Yıkama</Option>
                  <Option value="restaurant">Restoran</Option>
                  <Option value="equipment">Ekipman</Option>
                  <Option value="other">Diğer</Option>
                </Select>
              </Col>
              <Col xs={24} sm={12} md={6}>
                <Select
                  value={filters.lowStock}
                  onChange={(value) => setFilters({ ...filters, lowStock: value })}
                  style={{ width: '100%' }}
                  placeholder="Stok Durumu"
                  size={isMobile ? 'small' : 'middle'}
                >
                  <Option value="false">Tümü</Option>
                  <Option value="true">Düşük Stok</Option>
                </Select>
              </Col>
            </Row>
          </Card>

          <Card styles={{ body: { padding: isMobile ? '8px' : '24px' } }}>
            <Table
              columns={inventoryColumns}
              dataSource={inventories}
              rowKey="_id"
              loading={loading}
              pagination={{ 
                pageSize: 10,
                showSizeChanger: false,
                responsive: true
              }}
              scroll={{ x: 'max-content', y: isMobile ? '60vh' : undefined }}
              size={isMobile ? 'small' : 'middle'}
            />
          </Card>
        </>
      )
    },
    {
      key: 'transactions',
      label: 'İşlem Geçmişi',
      children: (
        <Card styles={{ body: { padding: isMobile ? '8px' : '24px' } }}>
          <Table
            columns={transactionColumns}
            dataSource={transactions}
            rowKey="_id"
            loading={transactionsLoading}
            pagination={{ 
              pageSize: 10,
              showSizeChanger: false,
              responsive: true
            }}
            scroll={{ x: 'max-content', y: isMobile ? '60vh' : undefined }}
            size={isMobile ? 'small' : 'middle'}
          />
        </Card>
      )
    }
  ];

  return (
    <div>
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={24} md={12}>
          <Title level={2} style={{ margin: 0, fontSize: isMobile ? '20px' : '24px' }}>
            <ShopOutlined /> Envanter Yönetimi
          </Title>
        </Col>
        <Col xs={24} md={12}>
          <Space direction={isMobile ? 'vertical' : 'horizontal'} style={{ width: isMobile ? '100%' : 'auto' }} size="small">
            {activeTab === 'inventory' && (
              <>
                <Button 
                  type="primary" 
                  icon={<PlusOutlined />} 
                  onClick={handleAddInventory} 
                  block={isMobile}
                >
                  Yeni Ürün Ekle
                </Button>
                <Button 
                  icon={<BarChartOutlined />} 
                  onClick={fetchStockReport} 
                  block={isMobile}
                >
                  Stok Raporu
                </Button>
                <Button 
                  icon={<FileExcelOutlined />}
                  onClick={() => handleExport(
                    () => stationOwnerAPI.exportInventoryExcel(),
                    `envanter_${Date.now()}.xlsx`,
                    () => message.success('Excel başarıyla indirildi')
                  )}
                  block={isMobile}
                >
                  Excel İndir
                </Button>
              </>
            )}
            {activeTab === 'transactions' && (
              <Button 
                type="primary" 
                icon={<PlusOutlined />} 
                onClick={() => handleAddTransaction()} 
                block={isMobile}
              >
                Yeni İşlem
              </Button>
            )}
          </Space>
        </Col>
      </Row>

      <Card>
        <Tabs
          activeKey={activeTab}
          onChange={setActiveTab}
          items={tabItems}
        />
      </Card>

      {/* Envanter Modal */}
      <Modal
        title={selectedInventory ? 'Envanter Düzenle' : 'Yeni Envanter Ürünü Ekle'}
        open={inventoryModalVisible}
        onCancel={() => {
          setInventoryModalVisible(false);
          setSelectedInventory(null);
          form.resetFields();
        }}
        footer={null}
        width={isMobile ? '95%' : 800}
        styles={{ body: { padding: isMobile ? '16px' : '24px', maxHeight: '80vh', overflowY: 'auto' } }}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmitInventory}
        >
          <Row gutter={[16, 16]}>
            <Col xs={24} sm={12}>
              <Form.Item
                name="stationId"
                label="İstasyon"
                rules={[{ required: true, message: 'İstasyon seçiniz' }]}
              >
                <Select placeholder="İstasyon seçiniz">
                  {stations.map((station: any) => (
                    <Option key={station._id} value={station._id}>
                      {station.name}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item
                name="productCode"
                label="Ürün Kodu"
                rules={[{ required: true, message: 'Ürün kodu gereklidir' }]}
              >
                <Input placeholder="Ürün kodu" />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item
                name="productName"
                label="Ürün Adı"
                rules={[{ required: true, message: 'Ürün adı gereklidir' }]}
              >
                <Input placeholder="Ürün adı" />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item
                name="brand"
                label="Marka"
              >
                <Input placeholder="Marka" />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item
                name="productType"
                label="Ürün Tipi"
                rules={[{ required: true }]}
              >
                <Select placeholder="Ürün tipi">
                  <Option value="fuel">Yakıt</Option>
                  <Option value="lubricant">Yağ</Option>
                  <Option value="market">Market</Option>
                  <Option value="car_wash">Oto Yıkama</Option>
                  <Option value="restaurant">Restoran</Option>
                  <Option value="equipment">Ekipman</Option>
                  <Option value="other">Diğer</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item
                name="unit"
                label="Birim"
                rules={[{ required: true }]}
              >
                <Select placeholder="Birim">
                  <Option value="litre">Litre</Option>
                  <Option value="kg">Kg</Option>
                  <Option value="adet">Adet</Option>
                  <Option value="paket">Paket</Option>
                  <Option value="kutu">Kutu</Option>
                  <Option value="m2">m²</Option>
                  <Option value="m3">m³</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col xs={24} sm={8}>
              <Form.Item
                name="currentStock"
                label="Mevcut Stok"
                rules={[{ required: true }]}
              >
                <InputNumber min={0} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col xs={24} sm={8}>
              <Form.Item
                name="minStockLevel"
                label="Min. Stok Seviyesi"
              >
                <InputNumber min={0} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col xs={24} sm={8}>
              <Form.Item
                name="maxStockLevel"
                label="Max. Stok Seviyesi"
              >
                <InputNumber min={0} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item
                name="costPrice"
                label="Maliyet Fiyatı"
              >
                <InputNumber min={0} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item
                name="sellingPrice"
                label="Satış Fiyatı"
              >
                <InputNumber min={0} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item
                name="supplierId"
                label="Tedarikçi"
              >
                <Select placeholder="Tedarikçi seçiniz" allowClear>
                  {suppliers.map((supplier: any) => (
                    <Option key={supplier._id} value={supplier._id}>
                      {supplier.companyName}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col xs={24}>
              <Form.Item
                name="notes"
                label="Notlar"
              >
                <TextArea rows={3} placeholder="Notlar" />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit">
                {selectedInventory ? 'Güncelle' : 'Oluştur'}
              </Button>
              <Button onClick={() => {
                setInventoryModalVisible(false);
                setSelectedInventory(null);
                form.resetFields();
              }}>
                İptal
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* Stok İşlemi Modal */}
      <Modal
        title="Stok İşlemi"
        open={transactionModalVisible}
        onCancel={() => {
          setTransactionModalVisible(false);
          transactionForm.resetFields();
        }}
        footer={null}
        width={isMobile ? '95%' : 600}
        styles={{ body: { padding: isMobile ? '16px' : '24px', maxHeight: '80vh', overflowY: 'auto' } }}
      >
        <Form
          form={transactionForm}
          layout="vertical"
          onFinish={handleSubmitTransaction}
        >
          <Form.Item
            name="inventoryId"
            label="Ürün"
            rules={[{ required: true, message: 'Ürün seçiniz' }]}
          >
            <Select placeholder="Ürün seçiniz" showSearch>
              {inventories.filter(inv => inv.isActive).map((inv: any) => (
                <Option key={inv._id} value={inv._id}>
                  {inv.productCode} - {inv.productName}
                </Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item
            name="transactionType"
            label="İşlem Tipi"
            rules={[{ required: true }]}
          >
            <Select>
              <Option value="receive">Alış</Option>
              <Option value="sale">Satış</Option>
              <Option value="return">İade</Option>
              <Option value="adjustment">Düzeltme</Option>
              <Option value="transfer">Transfer</Option>
              <Option value="waste">Fire</Option>
              <Option value="damage">Hasarlı</Option>
            </Select>
          </Form.Item>
          <Form.Item
            name="quantity"
            label="Miktar"
            rules={[{ required: true, message: 'Miktar giriniz' }]}
          >
            <InputNumber min={0} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item
            name="unitPrice"
            label="Birim Fiyat"
          >
            <InputNumber min={0} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item
            name="description"
            label="Açıklama"
          >
            <TextArea rows={3} placeholder="Açıklama" />
          </Form.Item>
          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit">
                İşlemi Gerçekleştir
              </Button>
              <Button onClick={() => {
                setTransactionModalVisible(false);
                transactionForm.resetFields();
              }}>
                İptal
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* Stok Raporu Modal */}
      <Modal
        title="Stok Raporu"
        open={reportModalVisible}
        onCancel={() => setReportModalVisible(false)}
        footer={null}
        width={isMobile ? '95%' : 800}
        styles={{ body: { padding: isMobile ? '16px' : '24px', maxHeight: '80vh', overflowY: 'auto' } }}
      >
        {stockReport && (
          <div>
            <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
              <Col xs={24} sm={12}>
                <Card>
                  <Statistic
                    title="Toplam Ürün"
                    value={stockReport.totalProducts}
                  />
                </Card>
              </Col>
              <Col xs={24} sm={12}>
                <Card>
                  <Statistic
                    title="Toplam Değer"
                    value={stockReport.totalValue}
                    precision={0}
                    suffix="₺"
                  />
                </Card>
              </Col>
              <Col xs={24} sm={12}>
                <Card>
                  <Statistic
                    title="Düşük Stok"
                    value={stockReport.lowStockItems}
                    valueStyle={{ color: '#ff4d4f' }}
                  />
                </Card>
              </Col>
              <Col xs={24} sm={12}>
                <Card>
                  <Statistic
                    title="Stokta Yok"
                    value={stockReport.outOfStockItems}
                    valueStyle={{ color: '#fa8c16' }}
                  />
                </Card>
              </Col>
            </Row>

            {stockReport.lowStockList && stockReport.lowStockList.length > 0 && (
              <Card title="Düşük Stok Ürünleri" style={{ marginBottom: 24 }}>
                <Table
                  dataSource={stockReport.lowStockList}
                  rowKey="productCode"
                  pagination={false}
                  size="small"
                  columns={[
                    { title: 'Ürün Kodu', dataIndex: 'productCode' },
                    { title: 'Ürün Adı', dataIndex: 'productName' },
                    { title: 'Mevcut Stok', dataIndex: 'currentStock' },
                    { title: 'Min. Stok', dataIndex: 'minStockLevel' },
                    { title: 'İstasyon', dataIndex: 'station' }
                  ]}
                />
              </Card>
            )}
          </div>
        )}
      </Modal>
    </div>
  );
};

export default Inventory;

